import { type GetStaticPaths, type GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { type ParsedUrlQuery } from 'querystring';

import { GoalName } from '../../__generated-gql-types__/globalTypes';
import Layout, {
  getStaticLayoutQuery,
  type LayoutDataProps,
} from '../../components/page/Layout';
import { WinWallHero } from '../../components/win-wall/hero/WinWallHero';
import {
  REVIEWS_PAGE_SIZE,
  ReviewSection,
} from '../../components/win-wall/reviews/ReviewSection';
import { type ReviewReviewFragment } from '../../components/win-wall/reviews/review/__generated-gql-types__/Review.generated';
import { initializeApollo } from '../../lib/apolloClient';
import { getReviewsPageRatingPathSegment } from '../../utils/routing';

import {
  type ReviewsOrganizationOutcomeSummaryFragment,
  useWinWallReviewsQuery,
  WinWallInitialDataDocument,
  type WinWallInitialDataQuery,
  type WinWallInitialDataQueryVariables,
} from './__generated-gql-types__/[[...goalAndRating]].generated';

interface ReviewsPageProps {
  goal: Possible<GoalName>;
  rating: Possible<number>;
  pageContentData: Possible<LayoutDataProps>;
  outcomes: ReviewsOrganizationOutcomeSummaryFragment[];
  globalTotalReviews: number;
  totalReviewsForGoal: number;
  reviewsByStarForGoal: number[];
  totalReviewsWithAllFilters: number;
  reviews: ReviewReviewFragment[];
}

const ReviewsPage: NextPageWithLayout<ReviewsPageProps> = ({
  goal,
  rating,
  outcomes,
  globalTotalReviews,
  totalReviewsForGoal,
  reviewsByStarForGoal,
  totalReviewsWithAllFilters,
  reviews,
}) => {
  const router = useRouter();

  const page =
    router.query.page && !Array.isArray(router.query.page)
      ? Math.max(parseInt(router.query.page), 1)
      : 1;

  const {
    data: {
      reviewsForMarketing: {
        results: reviewsFiltered,
        total: clientTotalReviewsWithAllFilters,
      } = {},
    } = {},
    loading,
  } = useWinWallReviewsQuery({
    variables: {
      goalName: goal,
      rating: rating,
      start: REVIEWS_PAGE_SIZE * (page - 1),
      count: REVIEWS_PAGE_SIZE,
    },
    // first page data is fetched in getStaticProps
    skip: page === 1,
  });

  return (
    <div className="pb-14">
      {/* Hero Section */}
      <div className="flex w-full flex-col text-center">
        <WinWallHero outcomes={outcomes} totalReviews={globalTotalReviews} />

        {/* Reviews Section */}
        <ReviewSection
          reviewsByStarForGoal={reviewsByStarForGoal}
          totalReviewsForGoal={totalReviewsForGoal}
          totalReviewsForPagination={
            clientTotalReviewsWithAllFilters ?? totalReviewsWithAllFilters
          }
          reviews={reviewsFiltered ?? reviews}
          page={page}
          ratingSearch={rating}
          goalSearch={goal}
          loading={loading}
        />
      </div>
    </div>
  );
};

ReviewsPage.getLayout = (pageContent, { pageContentData }) => {
  return (
    <Layout
      title="Coaching Reviews & Outcomes"
      description="Verified reviews, testimonials, and success stories from the Leland community across career development, school admissions, and test prep coaching."
      pageContentData={pageContentData}
      canonicalLink="/reviews"
    >
      {pageContent}
    </Layout>
  );
};

interface ReviewsPageParams extends ParsedUrlQuery {
  goalAndRating: string[];
}

export const getStaticPaths: GetStaticPaths<ReviewsPageParams> = () => {
  const goals = Object.values(GoalName).map((goal) => goal.toLowerCase());
  const ratings = Array.from({ length: 5 }, (_, i) => i + 1);
  const paths: string[][] = [[]];
  goals.forEach((goal) => {
    paths.push([goal]);
    ratings.forEach((rating) => {
      paths.push([goal, getReviewsPageRatingPathSegment(rating)]);
    });
  });
  ratings.forEach((rating) => {
    paths.push([getReviewsPageRatingPathSegment(rating)]);
  });

  return {
    paths: paths.map((goalAndRating) => ({
      params: { goalAndRating },
    })),
    fallback: false,
  };
};

const getRatingFromPathSegment = (pathSegment: string) => {
  const match = /^(\d)-stars?$/.exec(pathSegment);
  return match ? parseInt(match[1]) : null;
};

const getGoalAndRating = (
  goalAndRating: string[],
): Pick<ReviewsPageProps, 'goal' | 'rating'> => {
  if (goalAndRating.length === 0) {
    return { goal: null, rating: null };
  }

  const goal =
    Object.values(GoalName).find((g) => g.toLowerCase() === goalAndRating[0]) ??
    null;
  let rating: Nullable<number>;

  if (goalAndRating.length === 1) {
    rating = getRatingFromPathSegment(goalAndRating[0]);

    if (!goal && !rating) {
      throw new Error('Invalid goal and rating');
    }
  } else {
    rating = getRatingFromPathSegment(goalAndRating[1]);

    if (!goal || !rating) {
      throw new Error('Invalid goal or rating');
    }
  }

  return { goal, rating };
};

export const getStaticProps: GetStaticProps<
  ReviewsPageProps,
  ReviewsPageParams
> = async ({ params, revalidateReason }) => {
  const path = ['reviews', ...(params?.goalAndRating ?? [])].join('/');
  const log = (...args: unknown[]) => {
    console.log(`🏆 /${path} `, ...args);
  };

  try {
    log('getStaticProps, revalidateReason:', revalidateReason);
    let goal: Possible<GoalName>;
    let rating: Possible<number>;

    try {
      ({ goal, rating } = getGoalAndRating(params?.goalAndRating ?? []));
    } catch (e) {
      log('error getting goal and rating');
      console.error(e);
      return { notFound: true };
    }
    console.log('🏆 /reviews', { goal, rating });

    const pageContentData = await getStaticLayoutQuery();

    log('got pageContentData');

    const apolloClient = initializeApollo();
    log('initialized apollo client');
    const { data } = await apolloClient.query<
      WinWallInitialDataQuery,
      WinWallInitialDataQueryVariables
    >({
      query: WinWallInitialDataDocument,
      variables: { goalName: goal, rating },
    });

    log('got data');

    const {
      totalReviews = 0,
      reviewSummary: {
        totalReviews: totalReviewsForGoal = 0,
        reviewsByStar: reviewsByStarForGoal = [],
      } = {},
      organizationOutcomeSummaries: { results: outcomes = [] } = {},
      reviewsForMarketing: {
        results: reviews = [],
        total: totalReviewsWithAllFilters = 0,
      } = {},
    } = data ?? {};

    // shuffle outcomes so they are not sorted by count
    // Fisher-Yates shuffle - https://en.wikipedia.org/wiki/Fisher%E2%80%93Yates_shuffle
    const shuffledOutcomes = [...outcomes];
    for (let i = shuffledOutcomes.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = shuffledOutcomes[j];
      shuffledOutcomes[j] = shuffledOutcomes[i];
      shuffledOutcomes[i] = temp;
    }

    log('shuffled outcomes');
    log('done');

    return {
      props: {
        goal,
        rating,
        pageContentData,
        globalTotalReviews: totalReviews,
        totalReviewsForGoal,
        reviewsByStarForGoal,
        outcomes: shuffledOutcomes,
        totalReviewsWithAllFilters,
        reviews,
      },
      revalidate: 3600,
    };
  } catch (e) {
    log('unknown error');
    console.error(e);
    throw e;
  }
};

export default ReviewsPage;
